import React, { FC, useState } from 'react';
import classes from './ResourceSelection.module.scss';
import { Stack } from 'new/elements/Stack';
import { Checkbox, Radio } from '@mui/material';
import Typography from 'new/elements/Typography';
import { ChipWithCopy } from 'new/elements/ChipWithCopy';
import { Button } from 'new/elements/Button';
import DataSourceIcon from 'js/components/data_sources/DataSourceIcon';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import classnames from 'classnames';
import usePipelinesAdapter from 'new/state/pipelines/adapter/usePipelinesAdapter';
import { isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import DefaultExpandView from 'new/elements/ResourceSelectionButton/components/DefaultExpandView';
import { pipelineItemsSelector } from 'new/elements/ResourceSelectionButton/components/pipelinesItemSelector';

type Action = {
  label: string;
  onClick?: () => void;
  active: boolean;
};

export type ResourceSelectionItemProps = {
  multi?: boolean;
  previewActionLabel?: string;
  hideDetails?: boolean;
  hidePreviewAction?: boolean;
  disabled?: boolean;
  selected?: any[];
  item: any;
  onSelect: (item: any, checked: boolean) => void;
  resourceName: string;
  getSourceMeta: (id: string) => any;
  onClickPreview?: (item: any) => void;
  ExpandView?: FC<{ item: any }>;
  additionalActions?: Action[];
};

export const ResourceSelectionItem = (props: ResourceSelectionItemProps) => {
  const {
    multi = false,
    selected,
    item,
    previewActionLabel = 'Flow Preview',
    hidePreviewAction,
    onClickPreview: onPreviewClick = defaultPreviewClick,
    onSelect,
    disabled = false,
    resourceName,
    ExpandView,
    getSourceMeta,
  } = props;

  const [showExpand, setShowExpand] = useState(false);

  const { requestPipelineById } = usePipelinesAdapter();
  const pipelineItems = useSelector((state: any) => pipelineItemsSelector(state, item));

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = event.target;
    onSelect(item, checked);
  };

  if (!item) return null;

  const meta = getSourceMeta(item.id);

  function defaultPreviewClick() {
    // This is being kept just to keep what is working still working
    if (isEmpty(pipelineItems)) {
      requestPipelineById({
        id: item?.origin_node_id,
        isPreview: true,
        ignoreErrors: false,
      });
    }
  }

  const onClickPreview = () => {
    setShowExpand(!showExpand);
    onPreviewClick(item);
  };

  // Component to be shown in preview/expanded area - DefaultExpandView is showing flows view to keep things working
  const Expand = ExpandView || DefaultExpandView;

  return (
    <Stack
      className={classnames(classes.itemContainer, {
        [classes.itemContainer_noHover]: showExpand,
        [classes.itemContainer_disabled]: disabled,
      })}
    >
      <Stack padding="8px" direction="row" alignItems="center" justifyContent="space-between">
        <Stack flex={1} direction="row" alignItems="center" gap="8px">
          {multi ? (
            <Checkbox
              disabled={disabled}
              checked={selected.some((s) => s.id === item.id)}
              onChange={onChange}
            />
          ) : (
            <Radio
              disabled={disabled}
              checked={selected.some((s) => s.id === item.id)}
              onChange={onChange}
            />
          )}
          <DataSourceIcon
            // @ts-ignore
            icon={meta?.iconSm}
            nexset={resourceName === 'Nexset'}
          />
          <Stack>
            <Typography variant="bodyDefault">{item.name}</Typography>
            <Typography variant="bodySmall">{item.description}</Typography>
          </Stack>
        </Stack>
        <Stack direction="row" alignItems="center" gap="16px">
          <ChipWithCopy toCopy={item.id} tooltipTitle={`Copy ${resourceName} ID`}>
            ID {item.id}
          </ChipWithCopy>
          {!hidePreviewAction ? (
            <Button
              disabled={disabled}
              endIcon={showExpand ? <ExpandLess /> : <ExpandMore />}
              onClick={onClickPreview}
              size="small"
              variant={showExpand ? 'ghost' : 'outlined'}
              color="primary"
            >
              {previewActionLabel}
            </Button>
          ) : null}
          {/*{!hideDetails ? (*/}
          {/*  <Button*/}
          {/*    disabled={disabled}*/}
          {/*    endIcon={showDetails ? <ExpandLess /> : <ExpandMore />}*/}
          {/*    onClick={onClickDetails}*/}
          {/*    size="small"*/}
          {/*    variant={showDetails ? 'ghost' : 'outlined'}*/}
          {/*    color="primary">*/}
          {/*    Details*/}
          {/*  </Button>*/}
          {/*) : null}*/}
        </Stack>
      </Stack>
      {showExpand ? <Expand item={item} /> : null}
    </Stack>
  );
};
